export type FrozenChatReply = {
  projectId: string;
  chatId: string;
  clientRequestId: string;
  userMessageId: string;
  content: string;
  createdAt: string;
};

type Message = { id: string; role: string; content: string; created_at?: Date | string; metadata?: Record<string, any> };

/** Render cancelled output independently of the next attempt's streaming buffer. */
export function mergeFrozenChatReplies<T extends Message>(messages: T[], replies: FrozenChatReply[], projectId: string, chatId: string): T[] {
  const result = [...messages];
  for (const reply of replies) {
    if (reply.projectId !== projectId || reply.chatId !== chatId) continue;
    const userIndex = result.findIndex(message => message.id === reply.userMessageId);
    if (userIndex < 0) continue; // Never pull an unselected branch into view.
    const savedIndex = result.findIndex(message => message.role === 'assistant' && message.metadata?.clientRequestId === reply.clientRequestId);
    if (savedIndex >= 0) {
      const saved = result[savedIndex];
      result[savedIndex] = { ...saved, content: saved.content?.trim() ? saved.content : reply.content,
        metadata: { ...saved.metadata, agentInterrupted: true, triggerUserMessageId: reply.userMessageId } };
      continue;
    }
    if (!reply.content.trim()) continue;
    let index = userIndex + 1;
    while (index < result.length && result[index].role === 'assistant'
      && new Date(result[index].created_at ?? 0).getTime() <= new Date(reply.createdAt).getTime()) index++;
    result.splice(index, 0, {
      id: `cancelled-${reply.clientRequestId}`, role: 'assistant', content: reply.content,
      created_at: new Date(reply.createdAt), chat_id: chatId, user_id: '',
      metadata: { clientRequestId: reply.clientRequestId, triggerUserMessageId: reply.userMessageId, agentInterrupted: true },
    } as unknown as T);
  }
  return result;
}

// A stopped reply may not have reached persistence yet. Keep a same-tab recovery
// copy across refreshes, until the matching request has nonempty saved content.
const STORAGE_KEY = 'chat-frozen-replies-v1';
export function readFrozenChatReplies(): FrozenChatReply[] {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]');
    if (!Array.isArray(value)) return [];
    return value.filter((reply): reply is FrozenChatReply => reply &&
      ['projectId', 'chatId', 'clientRequestId', 'userMessageId', 'content', 'createdAt']
        .every(key => typeof reply[key] === 'string') && Number.isFinite(Date.parse(reply.createdAt)));
  } catch { return []; }
}
export function saveFrozenChatReplies(replies: FrozenChatReply[]): void {
  try {
    if (replies.length) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(replies));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* Storage can be unavailable; retain the in-memory recovery copy. */ }
}
